import React, { useEffect } from 'react';
import SEO from '../SEO';
import seoData from '../../seoData';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { CheckCircle, ArrowRight, Shield, Sparkles, Wind } from 'lucide-react';
import Header from '../Header';
import Footer from '../Footer';
import PageHero from '../ui/PageHero';
import { useNavigate } from 'react-router-dom';

const IndustrialCleaning = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <SEO title={seoData["services/industrial-cleaning"]?.title} description={seoData["services/industrial-cleaning"]?.description} path="/services/industrial-cleaning" keywords={seoData["services/industrial-cleaning"]?.keywords || []} />

      <PageHero
        badge="Core Services"
        title="Surface Preparation & Media Blasting"
        description="Industrial surface preparation and media blasting contractor in California. Abrasive blasting to SSPC/NACE standards, pressure washing, and chemical cleaning for coating applications on tanks, pipes, steel structures, and concrete."
        backgroundKey="industrial-cleaning"
        buttonText="Get In Touch"
      />

      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Problems We Solve</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {['Existing coating failure requiring removal before recoating',
              'Mill scale, rust, and corrosion products on steel surfaces',
              'Surface contamination preventing proper coating adhesion',
              'Confined space surfaces requiring specialized blasting methods',
              'Concrete laitance and surface preparation for coating',
              'Environmental and containment requirements for blasting operations'].map((challenge, index) => (
              <div key={index} className="flex items-start p-4 bg-slate-50 rounded-lg">
                <Sparkles className="text-slate-600 mr-3 mt-0.5 flex-shrink-0" size={20} />
                <span className="text-gray-700 text-sm">{challenge}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Our Solutions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Card className="border-l-4 border-l-slate-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Wind className="text-slate-600 mr-3 flex-shrink-0" size={22} />
                  Abrasive Blasting (SSPC-SP6/SP10)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Commercial and near-white metal blast cleaning to SSPC-SP6 and SP10 standards using steel grit, garnet, and aluminum oxide abrasives. Achieve required surface profile for optimal coating adhesion.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-slate-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Wind className="text-slate-600 mr-3 flex-shrink-0" size={22} />
                  Wet Abrasive Blasting
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Dustless wet blasting for environments where airborne dust is restricted. Ideal for occupied facilities, food plants, and urban locations where containment of dry abrasive is impractical.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-slate-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Wind className="text-slate-600 mr-3 flex-shrink-0" size={22} />
                  High-Pressure Water Jetting
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">UHP water jetting from 10,000-40,000 PSI for coating removal, concrete surface preparation, and industrial cleaning. No abrasive media, no dust — clean removal of coatings and contaminants.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-slate-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Wind className="text-slate-600 mr-3 flex-shrink-0" size={22} />
                  Concrete Surface Preparation
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Diamond grinding, shot blasting, and scarification for concrete floor and surface preparation. Achieve required surface profile for epoxy, polyurethane, and resinous coating systems.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Why Choose BCS</h2>
              <div className="space-y-3">
                {['On-site mobile blasting — we bring equipment to your facility',
                  'SSPC and NACE certified surface preparation methods',
                  'Full containment and environmental compliance',
                  'Confined space blasting certified crews',
                  'Surface profile measurement and verification included',
                  'Integrated with coating application — one contractor for prep and coating',
                  'Wet and dry methods for any environment',
                  'Concrete and steel surface preparation capabilities'].map((benefit, index) => (
                  <div key={index} className="flex items-start">
                    <CheckCircle className="text-green-700 mr-3 mt-0.5 flex-shrink-0" size={18} />
                    <span className="text-gray-700 text-sm">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-slate-700 text-white p-8 rounded-lg">
              <Shield className="mb-4" size={48} />
              <h3 className="text-xl font-bold mb-4">Turnkey Service</h3>
              <p className="text-slate-100 mb-4">BCS provides complete turnkey service from assessment through final QA/QC documentation. One contractor, one contract, done right.</p>
              <Button className="bg-white text-slate-700 hover:bg-gray-100" onClick={() => navigate('/#contact')}>
                Get Started <ArrowRight className="ml-2" size={16} />
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-blue-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">Proper Prep = Coating That Lasts</h2>
          <p className="text-blue-100 mb-8">Contact us for surface preparation services. 90% of coating failures are caused by inadequate surface prep — we get it right the first time.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-white text-blue-900 hover:bg-gray-100" onClick={() => window.location.href = 'tel:+19493719948'}>
              Call (949) 371-9948
            </Button>
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-blue-800" onClick={() => navigate('/#contact')}>
              Request Assessment <ArrowRight className="ml-2" size={20} />
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default IndustrialCleaning;
